import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import MyPageEditPresenter from './MyPageEditPresenter';
import { MyAccountContext } from '../../MyAccountContainer';
import Loading from '@components/Loading';
import { EditProfileInputField } from '@modules/mypage';
import useRequest from '@hooks/useRequest';
import useClient from '@hooks/useClient';
import useMyPage from '@hooks/useMyPage';
import useUserActionLog, {
  Enum_Action_Button_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import {
  STORAGE_ACTION,
  getStorageItem,
  clearStorageAll,
  authStorageClear
} from '@utils/storage';
import {
  CapaErrorCode,
  useUpdateQuotationUserMutation,
  useChangeEmailMutation
} from '@generated/graphql';

const MyPageEditContainer = () => {
  const context = useContext(MyAccountContext);
  const isGhost = getStorageItem(STORAGE_ACTION.IS_GHOST_CLIENT);
  const { onInsertUserActionLogOne } = useUserActionLog();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { onInitClient } = useClient();
  const { onInitRfqState } = useRequest();
  const { onInitProfileField } = useMyPage();
  const [changeUserEmail] = useChangeEmailMutation();
  const [updateQuotationUser, { loading }] = useUpdateQuotationUserMutation({
    refetchQueries: ['fetchMyQuotationUser']
  });

  const methods = useForm<EditProfileInputField & { authCode?: string }>();

  const onSubmit = (
    formState: EditProfileInputField & { authCode: string }
  ) => {
    if (isGhost && isGhost === 'true') return;

    if (formState.email) {
      changeUserEmail({
        variables: {
          input: {
            newEmail: formState.email,
            code: formState.authCode
          }
        }
      })
        .then((result) => {
          if (result.data?.changeEmail.result === CapaErrorCode.Success) {
            context.getClientQueryRefetch && context.getClientQueryRefetch();
            enqueueSnackbar(`이메일이 변경되었습니다.`, {
              variant: 'success'
            });
            clearStorageAll();
            authStorageClear();
            onInitClient();
            onInitRfqState();
            history.push('/login');
          } else {
            enqueueSnackbar('인증번호를 다시 확인해주세요.', {
              variant: 'error'
            });
          }
        })
        .catch((err) => {
          enqueueSnackbar(`수정을 실패하였습니다`, {
            variant: 'error'
          });
        });
    } else {
      if (formState.department || formState.position) {
        onInsertUserActionLogOne(
          Enum_Action_Button_Enum.Button_Mypage_Update_IsBiz_Submit,
          CapaActivityType.ButtonClick
        );
      }

      updateQuotationUser({
        variables: {
          input: {
            ...formState
          }
        }
      })
        .then((result) => {
          const successUpdate =
            result.data?.updateQuotationUser.result === CapaErrorCode.Success;
          if (successUpdate) {
            context.getClientQueryRefetch && context.getClientQueryRefetch();
            enqueueSnackbar(`사업자정보가 변경되었습니다.`, {
              variant: 'success'
            });
            history.push('/mypage/profile');
          }
        })
        .catch((e) => {
          enqueueSnackbar(`${formState} 수정을 실패하였습니다.`, {
            variant: 'error'
          });
        });
    }
  };

  const onCancel = () => {
    onInitProfileField();
    history.push('/mypage/profile');
  };

  const onInvaild: SubmitErrorHandler<
    EditProfileInputField & { authCode?: string }
  > = ({ managerName, cellphoneNumber }) => {
    if (managerName) {
      enqueueSnackbar('특수문자 없이 20자 이내로 입력하셔야 합니다.', {
        variant: 'error'
      });
    }
    if (cellphoneNumber) {
      enqueueSnackbar('휴대폰 번호는 11자리 숫자로 입력해주세요.', {
        variant: 'error'
      });
    }
  };

  return (
    <React.Fragment>
      <Loading open={loading} />
      <FormProvider {...methods}>
        <MyPageEditPresenter
          onSubmit={methods.handleSubmit(onSubmit, onInvaild)}
          onCancel={onCancel}
        />
      </FormProvider>
    </React.Fragment>
  );
};

export default MyPageEditContainer;
